import type { QuantityChangePreview } from '../orderhistory/quantityChangeTypes';
import { getAccessToken } from '../authentication/supabaseService';
import { readSseStream } from '../../utils/sseUtils';
import { getUserIdsWithSetting } from './superAdminSettingsService';

const API_BASE = '/api/admin';

// --- Code-monkey handoff (super-admin only) ------------------------------------------
// Posts an issue to a localhost listener running on the operator's own machine, which
// opens an interactive Claude Code session in a Terminal window to resolve it.
// The listener is z_ralph/codemonkey/server.mjs (started via `./cloudralph monkey`).
const CODEMONKEY_URL = process.env.REACT_APP_CODEMONKEY_URL || 'http://localhost:4123';
const CODEMONKEY_SECRET = process.env.REACT_APP_CODEMONKEY_SECRET || '';

export interface CodeMonkeyHandoff {
  flow: string; // which resolution flow / base prompt to use (e.g. 'email-issue')
  caseInfo: string; // human-readable case context appended to the base prompt
  context?: Record<string, unknown>; // structured metadata (stored in meta.json)
  // files staged locally by the listener: either a URL to download, or inline base64 (e.g. a picked logo)
  files?: ({ name: string; url: string } | { name: string; dataBase64: string })[];
}

export async function sendIssueToCodeMonkey(
  payload: CodeMonkeyHandoff,
): Promise<{ ok: boolean; taskId: string; taskDir: string }> {
  const response = await fetch(`${CODEMONKEY_URL}/handoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, secret: CODEMONKEY_SECRET }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Code-monkey handoff failed (${response.status}): ${text}`);
  }
  return response.json();
}

export interface AdminSummaryStats {
  totalOrders: number;
  totalUsers: number;
  uniquePlatforms: number;
  activeSubscriptions: number;
}

export interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
}

export interface AdminUserWithStaff {
  id: string;
  email: string;
  name: string | null;
  subscription_status: string;
  created_at: string;
  staff: StaffUser[];
}

export interface AdminUserStats {
  totalUsers: number;
  activeSubscriptions: number;
  adminUsers: AdminUserWithStaff[];
}

export interface PlatformOrderStats {
  platform: string;
  orderCount: number;
  totalSpent: number;
}

export interface WeeklyOrderStats {
  week: string;
  orderCount: number;
  totalSpent: number;
}

export interface AdminOrderStats {
  totalOrders: number;
  uniquePlatforms: string[];
  ordersByPlatform: PlatformOrderStats[];
  ordersByWeek: WeeklyOrderStats[];
}

export interface SourceTypeStats {
  sourceType: string;
  totalRevenue: number;
  entryCount: number;
}

export interface WeeklyRevenueStats {
  week: string;
  totalRevenue: number;
  entryCount: number;
}

export interface AdminRevenueStats {
  totalRevenueTracked: number;
  totalRevenueEntries: number;
  revenueBySourceType: SourceTypeStats[];
  revenueByWeek: WeeklyRevenueStats[];
}

async function fetchWithAuth(endpoint: string, excludeDemoAccounts: boolean = true) {
  const token = await getAccessToken();

  // Get demo user IDs from database settings to exclude from stats
  let excludeParam = '';
  if (excludeDemoAccounts) {
    try {
      const demoUserIds = await getUserIdsWithSetting('demo_account');
      if (demoUserIds.length > 0) {
        excludeParam = `?excludeUserIds=${encodeURIComponent(JSON.stringify(demoUserIds))}`;
      }
    } catch (e) {
      // If we can't fetch settings, proceed without exclusion
      console.warn('Could not fetch demo account settings:', e);
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}${excludeParam}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  return response.json();
}

export async function getAdminSummaryStats(): Promise<AdminSummaryStats> {
  return fetchWithAuth('/summary');
}

export async function getAdminUserStats(): Promise<AdminUserStats> {
  return fetchWithAuth('/users');
}

export async function getAdminOrderStats(): Promise<AdminOrderStats> {
  return fetchWithAuth('/orders');
}

export async function getAdminRevenueStats(): Promise<AdminRevenueStats> {
  return fetchWithAuth('/revenue');
}

export interface AdminRestaurant {
  id: string;
  name: string;
  ownerEmail: string;
}

/**
 * Get all restaurants across all users (for test report dropdown)
 */
export async function getAdminRestaurantsList(): Promise<AdminRestaurant[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/restaurants-list`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`Failed to fetch restaurants: ${response.statusText}`);
  return response.json();
}

/**
 * Send a fully custom one-off email composed by the super admin.
 * The HTML body is whatever the admin pastes in (not tied to restaurant data).
 * Both the HTML body and the plain-text fallback are required.
 */
export async function sendCustomEmail(
  recipients: string[],
  subject: string,
  htmlBody: string,
  textBody: string
): Promise<{ success: boolean; message: string }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/send-custom-email`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ recipients, subject, htmlBody, textBody }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send custom email: ${response.statusText}`);
  }
  return response.json();
}

export interface SetupQuantitySource { restaurantId: string; fileId: string; platform: string; }

export interface FlaggedProduct {
  /** Uploaded setup file; no email audit row or real Gmail message exists. */
  setupSource?: SetupQuantitySource;
  articleId: string;
  productName: string;
  platform: string;
  reason: string;
  quantity: number;
  rawQuantity: number;
  unitPrice: number;
  quantityInfo: {
    weightString: string;
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  } | null;
  gmailId: string | null;
  /** Which table this line came from. Absent on rows written before this spec → treat as 'pakbon'. */
  source?: 'pakbon' | 'factuur';
  /** Set true once an operator marks this flag done (flipped in place in the email's quantity_flags JSONB). */
  resolved?: boolean;
  /** Set true once an operator files this flag as a known to-do (instead of done), under `knownCategory`. */
  known?: boolean;
  knownCategory?: string;
}

export interface QuantityAuditResult {
  flaggedProducts: FlaggedProduct[];
  totalProductsScanned: number;
  totalFlagged: number;
  aiCost: {
    inputTokens: number;
    outputTokens: number;
    totalCostEur: number;
  };
}

export interface QtyFlagResolvedRestaurant { restaurantId: string; restaurantName: string; }
export interface QtyFlagCustomerRequest { id: string; restaurantId: string; restaurantName: string; status: string; createdAt: string; dismissedByAll: boolean; }

export interface FlaggedProductWithRestaurant extends FlaggedProduct {
  /** NOT a restaurants.id on AI Checker flags: an audit row id or setup-file:<rid>:<fid>. See resolvedRestaurants. */
  restaurantId: string;
  restaurantName: string;
  /** The real restaurants behind this flag; empty when they could not be resolved. */
  resolvedRestaurants?: QtyFlagResolvedRestaurant[];
  /** Newest customer question per restaurant for this product. */
  customerRequests?: QtyFlagCustomerRequest[];
}

export interface QuantityAuditReasonGroup {
  reason: string;
  count: number;
  products: FlaggedProductWithRestaurant[];
}

export interface QuantityAuditPlatformGroup {
  platform: string;
  count: number;
  reasonGroups: QuantityAuditReasonGroup[];
}

export interface GroupedQuantityAuditResult {
  platformGroups: QuantityAuditPlatformGroup[];
  restaurantsScanned: number;
  totalProductsScanned: number;
  totalFlagged: number;
  aiCost: {
    inputTokens: number;
    outputTokens: number;
    totalCostEur: number;
  };
}

/**
 * Run AI-powered audit on product quantity info for a restaurant
 */
export async function runQuantityAudit(
  restaurantId: string,
  startDate: string,
  endDate: string
): Promise<QuantityAuditResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/audit-quantity-info`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ restaurantId, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run quantity audit: ${response.statusText}`);
  }
  return response.json();
}

export interface IssueTypeGroup {
  issueType: string;
  count: number;
  products: FlaggedProductWithRestaurant[];
}

export interface PlatformIssueGroups {
  platform: string;
  count: number;
  issueGroups: IssueTypeGroup[];
}

export interface GroupedIssuesResult {
  platformGroups: PlatformIssueGroups[];
  totalFlagged: number;
  aiCost: {
    inputTokens: number;
    outputTokens: number;
    totalCostEur: number;
  };
}

/**
 * Second AI pass: cluster already-flagged products into AI-named issue types, one Sonnet call
 * per supplier. Pure grouping layer — does not re-run the audit.
 */
export async function groupQuantityIssuesByType(
  flaggedProducts: FlaggedProductWithRestaurant[]
): Promise<GroupedIssuesResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/audit-quantity-info/group-issues`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ flaggedProducts }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to group quantity issues: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Run the quantity-info audit across ALL restaurants, grouped by platform → reason.
 */
export async function runQuantityAuditAllRestaurants(
  startDate: string,
  endDate: string
): Promise<GroupedQuantityAuditResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/audit-quantity-info-all`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run cross-restaurant quantity audit: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Resolve which Gmail mailbox (cloud63 / cloud64) holds a message, by its RFC822 Message-ID.
 * Returns null when the message can't be found in either inbox.
 */
export async function findGmailAccount(
  rfc822MessageId: string
): Promise<'cloud63' | 'cloud64' | null> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/find-gmail-account`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rfc822MessageId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to find gmail account: ${response.statusText}`);
  }
  const data = await response.json();
  return data.account ?? null;
}

export interface AiCheckerEmailAttachment {
  index: number;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

/**
 * List the attachments of one Gmail message (metadata only — nothing is downloaded until the
 * operator opens a file). Used by the AI Checker detail view.
 */
export async function listAiCheckerEmailAttachments(
  account: string,
  gmailMessageId: string
): Promise<AiCheckerEmailAttachment[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/email-attachments`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, gmailMessageId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to list email attachments: ${response.statusText}`);
  }
  const data = await response.json();
  return data.attachments ?? [];
}

/**
 * Fetch ONE attachment of a Gmail message (by its index from listAiCheckerEmailAttachments) as a
 * Blob, streamed straight from Gmail — the caller opens it via an object URL.
 */
export async function fetchAiCheckerEmailAttachment(
  account: string,
  gmailMessageId: string,
  index: number
): Promise<Blob> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/email-attachment`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, gmailMessageId, index }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch email attachment: ${response.statusText}`);
  }
  return response.blob();
}

export interface AiCheckerEmailHandoffFile {
  filename: string;
  mimeType: string;
  fileText: string | null;
  dataBase64: string | null;
}

export interface AiCheckerEmailHandoffFiles {
  bodyText: string;
  files: AiCheckerEmailHandoffFile[];
}

/** One email's body text and attachments (extracted text + base64), to hand to a code-monkey session. */
export async function fetchAiCheckerEmailHandoffFiles(
  account: string,
  gmailMessageId: string
): Promise<AiCheckerEmailHandoffFiles> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/email-handoff-files`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, gmailMessageId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to prepare email files: ${response.statusText}`);
  }
  return response.json();
}

export interface RestaurantForProductOverride {
  restaurantId: string;
  restaurantName: string;
  restaurantPlatformId: string;
  occurrences: number;
  hasOverride: boolean;
}

/**
 * List every restaurant that has ordered a given product (matched the same way overrides are keyed),
 * so an operator can scope a quantity-info override to specific restaurants.
 */
export async function getRestaurantsForProductOverride(
  platform: string,
  articleId: string | null | undefined,
  productName: string
): Promise<RestaurantForProductOverride[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/quantity-override/affected-restaurants`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ platform, articleId, productName }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to list affected restaurants: ${response.statusText}`);
  }
  const data = await response.json();
  return data.restaurants ?? [];
}

/** What a quantity override would do at each selected restaurant, before doing it. */
export async function previewQuantityOverride(args: {
  platform: string;
  articleId: string | null | undefined;
  productName: string;
  stdAmount: number;
  stdMultiplier: number;
  stdUnit: string;
  weightString?: string;
  restaurantPlatformIds: string[];
}): Promise<{ previews: Array<{ restaurantPlatformId: string; preview: QuantityChangePreview }>; failed: string[] }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/quantity-override/preview`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to preview quantity override: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Apply a quantity-info override to selected restaurants (backfills existing orders + auto-applies to
 * future invoices). Returns how many restaurants and orders were updated.
 */
export async function applyQuantityOverride(args: {
  platform: string;
  articleId: string | null | undefined;
  productName: string;
  stdAmount: number;
  stdMultiplier: number;
  stdUnit: string;
  weightString?: string;
  restaurantPlatformIds: string[];
  previewFingerprints?: Record<string, string>;
}): Promise<{ restaurantsApplied: number; totalUpdated: number; failed: Array<{ restaurantPlatformId: string; error: string }> }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/quantity-override/apply`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to apply quantity override: ${response.statusText}`);
  }
  return response.json();
}

export interface DuplicateProductVariant {
  productName: string;
  lineCount: number;
  lastOrderDate: string;
  latestPrice: number | null;
}

export interface DuplicateProductGroup {
  restaurantId: string;
  restaurantName: string;
  platform: string;
  products: DuplicateProductVariant[];
}

export interface DuplicateScanResult {
  groups: DuplicateProductGroup[];
  totalProductsScanned: number;
  restaurantsScanned: number;
}

export interface CombineDuplicatesResult {
  ordersUpdated: number;
  costPriceUpdated: number;
  inventoryKeysUpdated: number;
  quantityOverridesUpdated: number;
  priceAlertsUpdated: number;
  aliasesCreated: number;
}

/**
 * Scan for near-duplicate products on no-articleId platforms. Scoped to one
 * restaurant when `restaurantId` is given, otherwise scans all restaurants.
 */
export async function scanDuplicateProducts(restaurantId?: string): Promise<DuplicateScanResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/duplicate-products/scan`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(restaurantId ? { restaurantId } : {}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to scan for duplicates: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Combine duplicate product variants into one canonical name (renames order
 * history + all name-keyed data, and stores aliases for future invoices).
 */
export async function combineDuplicateProducts(
  restaurantId: string,
  platform: string,
  canonicalName: string,
  variantNames: string[]
): Promise<CombineDuplicatesResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/duplicate-products/combine`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ restaurantId, platform, canonicalName, variantNames }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to combine duplicates: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Mark a scanned duplicate group as NOT duplicates (skipped on future scans).
 */
export async function ignoreDuplicateProducts(
  restaurantId: string,
  platform: string,
  productNames: string[]
): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/duplicate-products/ignore`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ restaurantId, platform, productNames }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to ignore duplicates: ${response.statusText}`);
  }
}

/** One invoice a Moët variant was seen on, with the source PDF to verify it against. */
export interface MoetDuplicateSource {
  orderId: string;
  orderNumber: string;
  orderDate: string;
  fileUrl: string | null;
  productName: string;
  price: number | null;
  quantity: number;
  /** >1 when this PDF holds several invoices — a stacked scan, the usual source of mis-parses. */
  invoicesInFile: number;
}

export interface MoetDuplicateVariant {
  articleId: string;
  productName: string;
  lineCount: number;
  orderCount: number;
  totalQuantity: number;
  firstOrderDate: string;
  lastOrderDate: string;
  latestPrice: number | null;
  stdAmount: number | null;
  stdUnit: string | null;
  sources: MoetDuplicateSource[];
  sourcesTruncated: boolean;
}

export interface MoetDuplicateGroup {
  restaurantId: string;
  restaurantName: string;
  platform: string;
  /** Most-seen first — the default base product. */
  variants: MoetDuplicateVariant[];
}

export interface MoetDuplicateScanResult {
  groups: MoetDuplicateGroup[];
  totalProductsScanned: number;
  restaurantsScanned: number;
}

export interface MoetMergeResult {
  ordersUpdated: number;
  factuurOrdersUpdated: number;
  costPriceUpdated: number;
  subrecipeIngredientsUpdated: number;
  inventoryKeysUpdated: number;
  inventoryCountsUpdated: number;
  quantityOverridesUpdated: number;
  priceAlertsUpdated: number;
  wasteUpdated: number;
  /** Alternatives that became the same product as their own primary and were dropped. */
  alternativesRemoved: number;
  /** Dishes/subrecipes now holding the base product on two ingredient rows — manual cleanup. */
  duplicateIngredientRows: string[];
}

/**
 * Scan Moët Hennessy order history for mis-scanned duplicate article ids. Scoped to one
 * restaurant when `restaurantId` is given, otherwise scans all restaurants.
 */
export async function scanMoetDuplicates(restaurantId?: string): Promise<MoetDuplicateScanResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/moet-duplicates/scan`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(restaurantId ? { restaurantId } : {}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to scan Moët duplicates: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Rewrite the chosen duplicate article ids (and their names) into the canonical pair across every
 * table that persists the product identity.
 */
export async function mergeMoetDuplicates(
  restaurantId: string,
  canonicalArticleId: string,
  canonicalName: string,
  duplicateArticleIds: string[]
): Promise<MoetMergeResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/moet-duplicates/merge`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ restaurantId, canonicalArticleId, canonicalName, duplicateArticleIds }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to merge Moët duplicates: ${response.statusText}`);
  }
  return response.json();
}

export interface DataExportResult {
  markdown: string;
  filename: string;
  restaurantName: string;
}

/**
 * Build a full markdown data export (overview, revenue, spending, cost price)
 * for a restaurant over a date range.
 */
export async function exportRestaurantData(
  restaurantId: string,
  startDate: string,
  endDate: string
): Promise<DataExportResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/export-restaurant-data`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ restaurantId, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to export data: ${response.statusText}`);
  }
  return response.json();
}

export type AdminReportType =
  | 'revenue'
  | 'weekly'
  | 'monthly'
  | 'waste'
  | 'inventoryLeakage'
  | 'inventoryOrder'
  | 'inventoryManagement'
  | 'priceAlerts';

/** Language the report PDF, its filename and the summary email are rendered in. */
export type AdminReportLanguage = 'en' | 'nl';

export interface GeneratedReportFile {
  pdfBase64: string;
  filename: string;
}

export interface GenerateReportFileResult {
  files: GeneratedReportFile[];
  restaurantName: string;
}

/**
 * Preview-only override of the leakage report's quantity unit (super-admin tools only). Mirrors
 * `LeakageReportUnitMode` on the settings side; kept separate because this one is never stored.
 */
export type LeakageUnitPreview = 'std' | 'count';

/**
 * Generate PDF report file(s) for a restaurant over a date range, rendered in `language`.
 * Usually one file; 'inventoryLeakage' returns the leakage report.
 * The inventory report types ignore startDate/endDate — their period is derived from the last
 * inventory counts. 'inventoryOrder' and 'inventoryManagement' cover a single inventory group and
 * require `groupId`; 'inventoryLeakage' takes one optionally (omitted = all groups combined) and, with a
 * group, accepts `baselineSessionId` to measure from an older count than the previous one.
 *
 * `leakageUnitMode` renders the leakage report's QUANTITIES in the given unit for this run only
 * ('std' = kg / L / pcs, 'count' = the starred count unit). It does NOT write the restaurant's
 * setting, so both outputs can be compared for a live client; omitted = the stored setting.
 * Rejected by the server for any other report type.
 */
export async function generateReportFile(
  restaurantId: string,
  reportType: AdminReportType,
  startDate: string,
  endDate: string,
  language: AdminReportLanguage,
  groupId?: string,
  baselineSessionId?: string,
  leakageUnitMode?: LeakageUnitPreview,
  counts: { countSessionId?: string; endingCountId?: string } = {}
): Promise<GenerateReportFileResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/generate-report-file`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      restaurantId, reportType, startDate, endDate, language, groupId, baselineSessionId, leakageUnitMode, ...counts,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to generate report file: ${response.statusText}`);
  }
  return response.json();
}

/**
 * One data-alert warning carried by a send: what was incomplete about the window the report
 * covers. Informational only — since phase 8 an open alert never holds a send, it is printed
 * in the email body and reported back here so the sender sees what went out with it.
 */
export interface ReportDataWarning {
  alertType: string;
  /** Server-rendered, already translated into the report's language. */
  message: string;
}

export interface SendReportEmailResult {
  success?: boolean;
  files: { filename: string }[];
  restaurantName: string;
  /** Addresses the email actually reached, and the ones it did not. */
  sent: string[];
  failed: string[];
  /** Deliberate delivery exclusions; absent on older server builds. */
  skipped?: { email: string; reason: 'restrict_main_pages' }[];
  /** Absent on an older server build; treat as "none". */
  dataWarnings?: ReportDataWarning[];
}

/**
 * Generate a PDF report for a restaurant over a date range and email it, rendered in
 * `language` (PDF, subject and body). Each recipient gets their own email.
 * `isTest` prefixes the subject with "[TEST] " — pass false only for a real client send.
 * Same reportType/date rules as generateReportFile.
 *
 * Open data alerts for the window never refuse the send: they come back as `dataWarnings` and
 * are printed in the email body.
 */
export async function sendReportEmail(
  restaurantId: string,
  reportType: AdminReportType,
  startDate: string,
  endDate: string,
  recipients: string[],
  language: AdminReportLanguage,
  isTest: boolean,
  groupId?: string,
  baselineSessionId?: string,
  leakageUnitMode?: LeakageUnitPreview,
  counts: { countSessionId?: string; endingCountId?: string } = {}
): Promise<SendReportEmailResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/send-report-email`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      restaurantId,
      reportType,
      startDate,
      endDate,
      recipients,
      language,
      isTest,
      groupId,
      baselineSessionId,
      leakageUnitMode,
      ...counts,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send report email: ${response.statusText}`);
  }
  return response.json();
}

/** Raw `users` columns, so the role fields keep their snake_case names from the row. */
export interface ReportRecipient {
  id: string;
  name: string | null;
  email: string;
  /** Super-admin-only classification; never shown to customers. Null when never set. */
  internal_role: string | null;
}

export interface ReportRecipientStaff extends ReportRecipient {
  /** The staff member's job title. Null when never set — set it on /admin/communication/roles. */
  restaurant_role: string | null;
}

export interface ReportRecipients {
  owner: ReportRecipient | null;
  staff: ReportRecipientStaff[];
}

/** The restaurant's owning admin + its assigned staff, for the real-send recipient picker. */
export async function getReportRecipients(restaurantId: string): Promise<ReportRecipients> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/report-recipients/${restaurantId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch report recipients: ${response.statusText}`);
  }
  return response.json();
}

/** The three communication templates the templated-email card can send. */
export type TemplatedEmailTemplate = 'product_update' | 'good_news' | 'needs_input';

/** One block of a rich product update email — mirrors the server's section model. */
export type ProductUpdateSection =
  | { type: 'sectionTitle'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'numberedSteps'; items: { lead?: string; text: string }[] }
  | { type: 'bulletList'; items: string[]; boxed?: boolean }
  | { type: 'image'; url: string; alt?: string }
  | { type: 'button'; label: string; url: string }
  | { type: 'divider' };

/** The rich, section-based content of a product update email. */
export interface ProductUpdateContent {
  /** Language of the server-owned strings (greeting, sign-off, footer). Defaults to 'en'. */
  language?: 'en' | 'nl';
  eyebrow?: string;
  preheader?: string;
  intro?: string;
  sections: ProductUpdateSection[];
  closingPanel?: { headline: string; buttonLabel: string; url: string };
}

export interface SendTemplatedEmailResult {
  sent: number;
  failed: number;
  failures: { email: string; error: string }[];
}

/**
 * Send a templated communication email, styled like the report emails.
 * Test mode mails a single typed-in address with a "[TEST] " subject prefix; real mode mails
 * one email per picked recipient — the server re-resolves the addresses from
 * `recipientUserIds`, plus validated `additionalEmails` for non-customers. `scope: 'all'` targets
 * restaurant admins across the whole customer base instead of one restaurant.
 * Product updates send `content` (rich sections); the other templates send `body`.
 */
export async function sendTemplatedEmail(args: {
  template: TemplatedEmailTemplate;
  title: string;
  subject?: string;
  body?: string;
  content?: ProductUpdateContent;
  isTest: boolean;
  testEmail?: string;
  scope?: 'all';
  restaurantId?: string;
  recipientUserIds?: string[];
  additionalEmails?: string[];
}): Promise<SendTemplatedEmailResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/send-templated-email`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({} as any));
    throw new Error(err.error || `Failed to send templated email: ${response.statusText}`);
  }
  return response.json();
}

/** One admin/staff account that may receive a communication email. */
export interface CommunicationRecipient {
  userId: string;
  /** Null when the account never set a name — fall back to the email. */
  name: string | null;
  email: string;
  /** `users.role`: 'admin' for the owning account, 'staff' for a staff account. */
  role: string;
  /** The staff member's job title. Null when never set (and for admins). */
  restaurantRole: string | null;
}

/**
 * The restaurant's admin + APPROVED staff accounts, for the templated-email recipient picker.
 * Pending/rejected staff are filtered out server-side — they must never receive client data.
 */
export async function getCommunicationRecipients(restaurantId: string): Promise<CommunicationRecipient[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/communication-recipients?restaurantId=${encodeURIComponent(restaurantId)}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch communication recipients: ${response.statusText}`);
  }
  const data = await response.json();
  return data.recipients ?? [];
}

/** One approved staff account nested under its admin in the "all restaurants" picker. */
export interface CommunicationStaffRecipient {
  userId: string;
  name: string | null;
  email: string;
  restaurantRole: string | null;
  primaryRestaurantId: string;
}

/** One restaurant-owning admin with their approved staff, for the "all restaurants" scope. */
export interface CommunicationAdminGroup {
  userId: string;
  name: string | null;
  email: string;
  restaurantNames: string[];
  primaryRestaurantId: string;
  staff: CommunicationStaffRecipient[];
}

/**
 * Every restaurant-owning admin with their approved staff nested underneath (hidden
 * restaurants excluded) — the "all restaurants" scope of a templated send. The card
 * pre-checks the admins; staff start unchecked and are toggled on freely.
 */
export async function getAllCommunicationRecipientGroups(): Promise<CommunicationAdminGroup[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/communication-recipients-all`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch recipients: ${response.statusText}`);
  }
  const data = await response.json();
  return data.recipients ?? [];
}

/** Keep these limits in sync with server/sendEmail/productUpdateEmail.ts. */
export const MAX_COMMUNICATION_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_COMMUNICATION_INLINE_BYTES = 10 * 1024 * 1024;

export function communicationInlineImageBytes(url: string): number {
  if (!url.startsWith('data:')) return 0;
  const base64 = url.slice(url.indexOf(',') + 1);
  return base64.length * 3 / 4 - (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0);
}

function readCommunicationImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string'
      ? resolve(reader.result)
      : reject(new Error('adminTesting.templatedEmail.composer.imageReadFailed'));
    reader.onerror = reader.onabort = () => reject(new Error('adminTesting.templatedEmail.composer.imageReadFailed'));
    reader.readAsDataURL(file);
  });
}

/** S3 first; on outage/timeout keep the selected image as a data URL for CID sending. */
export async function uploadCommunicationImage(file: File): Promise<string> {
  if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
    throw new Error('adminTesting.templatedEmail.composer.invalidImage');
  }
  if (!file.size || file.size > MAX_COMMUNICATION_IMAGE_BYTES) {
    throw new Error('adminTesting.templatedEmail.composer.imageTooLarge');
  }
  const token = await getAccessToken();
  const formData = new FormData();
  formData.append('image', file);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12_000);
  let response: Response;
  let data: { url?: string };
  try {
    response = await fetch(`${API_BASE}/communication-images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
      signal: controller.signal,
    });
    data = response.ok ? await response.json() : {};
  } catch (err) {
    console.error('Communication image upload unavailable; using inline image', err);
    return readCommunicationImage(file);
  } finally {
    clearTimeout(timeout);
  }
  if (response.status >= 500) return readCommunicationImage(file);
  // Permission/validation failures must not be disguised as storage outages.
  if (!response.ok || !data.url?.startsWith('https://')) {
    throw new Error('adminTesting.templatedEmail.composer.uploadFailed');
  }
  return data.url;
}

/** Render a templated email without sending — the composer's preview iframe. */
export async function previewTemplatedEmail(args: {
  template: TemplatedEmailTemplate;
  title: string;
  subject?: string;
  body?: string;
  content?: ProductUpdateContent;
}): Promise<{ html: string; subject: string }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/preview-templated-email`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  });
  const data = await response.json().catch(() => ({} as any));
  if (!response.ok) {
    throw new Error(data.error || `Failed to render preview: ${response.statusText}`);
  }
  return data;
}

/** Export the current draft, including images, without sending an email. */
export async function downloadProductUpdatePdf(args: { title: string; content: ProductUpdateContent }): Promise<Blob> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/product-update-pdf`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to download product update PDF');
  }
  return response.blob();
}

/** One logged outbound email — one row per recipient of a send. */
export interface EmailSendLogRow {
  /** BIGSERIAL: node-postgres hands int8 back as a string, so accept either. */
  id: string | number;
  sentAt: string;
  emailType: string;
  recipientEmail: string;
  userId: string | null;
  restaurantId: string | null;
  subject: string;
  status: 'sent' | 'failed';
  error: string | null;
  resendMessageId: string | null;
  isTest: boolean;
  triggeredBy: string;
  metadata: Record<string, unknown> | null;
  /**
   * Resend tracking (M80), written by the delivery webhook. All null means no event
   * has arrived — a send from before tracking shipped, a failed send, or simply mail
   * Resend hasn't reported on yet. It never means the mail failed to arrive.
   */
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  lastEvent: string | null;
}

export interface EmailSendLogFilters {
  emailType?: string;
  restaurantId?: string;
  /** Substring match on the recipient address. */
  recipient?: string;
  /** Inclusive 'YYYY-MM-DD' bounds on sent_at. */
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface EmailSendLogResult {
  rows: EmailSendLogRow[];
  /** Total matching the filters, ignoring limit/offset — drives the pager. */
  total: number;
}

/** Page through the outbound email log, newest first. */
export async function getEmailSendLog(filters: EmailSendLogFilters = {}): Promise<EmailSendLogResult> {
  const token = await getAccessToken();
  const params = new URLSearchParams();
  if (filters.emailType) params.set('emailType', filters.emailType);
  if (filters.restaurantId) params.set('restaurantId', filters.restaurantId);
  if (filters.recipient) params.set('recipient', filters.recipient);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  const query = params.toString();

  const response = await fetch(`${API_BASE}/email-send-log${query ? `?${query}` : ''}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch email send log: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Short-lived signed URL for one archived report file, by its S3 key (from a log row's
 * `metadata.files`). The server rejects keys outside `reports/` — never build a key client-side,
 * only pass one back that came from a log row.
 */
export async function getEmailSendLogFileUrl(key: string): Promise<string> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/email-send-log/file?key=${encodeURIComponent(key)}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to open file: ${response.statusText}`);
  }
  const data = await response.json();
  return data.url;
}

/** One saved inventory count of a group — a "compare against" option on the report card. */
export interface AdminInventoryGroupCount {
  sessionId: string;
  countIds: string[];
  /** ISO timestamp of the moment the count was completed. */
  completedAt: string;
  totalValue: number;
}

export interface AdminInventoryGroup {
  id: string;
  name: string;
  /** How many of the group's products have a real minimum stock level configured. */
  minimumsCount: number;
  /** The group's saved counts, newest first. */
  counts: AdminInventoryGroupCount[];
}

export interface AdminInventoryGroups {
  groups: AdminInventoryGroup[];
}

/** The restaurant's inventory groups, for the per-group inventory report selector. */
export async function getInventoryGroups(restaurantId: string): Promise<AdminInventoryGroups> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/inventory-groups/${restaurantId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch inventory groups: ${response.statusText}`);
  }
  return response.json();
}

export interface ProductInspectionRow {
  productName: string;
  articleId: string | null;
  price: string | null;
  quantity: string | null;
  rawQuantity: string | null;
  quantityInfo: unknown;
  occurrences: number;
}

/** An orderhistory (raw pakbon) inspection row + whether a linked factuur overrides it at read time. */
export interface OrderhistoryInspectionRow extends ProductInspectionRow {
  overriddenByFactuur: boolean;
}

/** A factuur_orders inspection row (what the app actually reads where linked) + platform/link status. */
export interface FactuurInspectionRow extends ProductInspectionRow {
  platform: string;
  linkedToOrder: boolean;
}

/** One inventory-section assignment for a product (which restaurant/section tracks it + unit config). */
export interface InventoryAssignmentRow {
  productKey: string;
  restaurantName: string;
  sectionName: string;
  countUnit: string | null;
  countUnitToStdFactor: string | null;
  stdUnit: string | null;
  /** Alternate count units + factors — must move together with std_unit on a stdUnit fix. */
  additionalCountUnits: unknown;
}

/** One frozen count line for a product, pulled from a saved inventory count's items[]. */
export interface InventoryCountItemRow {
  countedAt: string;
  restaurantName: string;
  sectionNameSnapshot: string | null;
  productKey: string;
  count: string | null;
  countUnit: string | null;
  /** The std unit the line was FROZEN in — compare against the corrected quantityInfo.stdUnit. */
  stdUnit: string | null;
  countInStdUnit: string | null;
  pricePerStdUnit: string | null;
  value: string | null;
}

/** A per-product minimum stock row (M63) — stored in the product's std unit. */
export interface InventoryMinimumRow {
  productKey: string;
  restaurantName: string;
  minStockCount: string | null;
  countUnit: string | null;
  minStockInStdUnit: string | null;
}

/** A manual inventory valuation price (M69) — €/std unit. */
export interface InventoryPriceOverrideRow {
  productKey: string;
  restaurantName: string;
  pricePerStdUnit: string | null;
  stdUnit: string | null;
}

export interface ProductInventoryData {
  inventoryAssignments: InventoryAssignmentRow[];
  inventoryCountItems: InventoryCountItemRow[];
  inventoryCountItemsTotalMatches: number;
  inventoryMinimums: InventoryMinimumRow[];
  inventoryPriceOverrides: InventoryPriceOverrideRow[];
}

/** Where a cost-price reference to the product lives (the four sites fixQuantities.md STEP 4 checks). */
export type CostPriceRefKind =
  | 'ingredient'
  | 'alternative'
  | 'subrecipe-ingredient'
  | 'subrecipe-alternative';

/**
 * One cost-price / subrecipe row referencing the product. `quantityNeeded` is expressed in the
 * snapshotted `quantityInfo`'s unit, so rows carrying the bad snapshot need a proportional conversion
 * when the order data is fixed.
 */
export interface CostPriceReferenceRow {
  refKind: CostPriceRefKind;
  restaurantName: string;
  /** Cost price item name, or the subrecipe name for the subrecipe kinds. */
  ownerName: string;
  productName: string | null;
  platform: string | null;
  articleId: string | null;
  quantityNeeded: string | null;
  quantityInfo: unknown;
  isCustom: boolean;
}

export interface ProductCostPriceData {
  costPriceRefs: CostPriceReferenceRow[];
  costPriceRefsTotalMatches: number;
}

export interface ProductInspectionResult {
  rows: {
    orderhistory: OrderhistoryInspectionRow[];
    factuurOrders: FactuurInspectionRow[];
  };
  inventory: ProductInventoryData;
  costPrice: ProductCostPriceData;
}

const EMPTY_INSPECTION_ROWS: ProductInspectionResult['rows'] = {
  orderhistory: [],
  factuurOrders: [],
};

const EMPTY_INVENTORY_DATA: ProductInventoryData = {
  inventoryAssignments: [],
  inventoryCountItems: [],
  inventoryCountItemsTotalMatches: 0,
  inventoryMinimums: [],
  inventoryPriceOverrides: [],
};

const EMPTY_COST_PRICE_DATA: ProductCostPriceData = {
  costPriceRefs: [],
  costPriceRefsTotalMatches: 0,
};

/**
 * Inspect distinct quantity-info variations for a product across all restaurants, plus whether the
 * issue reaches saved inventory data (assignments, frozen count lines, minimums, price overrides)
 * and cost-price setups (ingredients / alternatives / subrecipe ingredients referencing it).
 * Used to assemble the fix-quantities prompt for Claude Code.
 *
 * ALWAYS pass the flag's `platform` + `articleId`: they scope the inventory and cost-price lookups.
 * Name alone matches the same word on every platform, and the sentinel article ids those unrelated
 * products carry ('no_article_id', 'EMBALLAGE', …) then drag in whole suppliers' worth of rows.
 * `inventory` and `costPrice` degrade to empty arrays on older responses.
 */
export async function inspectProductQuantities(
  productName: string,
  scope?: { platform?: string | null; articleId?: string | null },
): Promise<ProductInspectionResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/inspect-product-quantities`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ productName, platform: scope?.platform, articleId: scope?.articleId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to inspect product quantities: ${response.statusText}`);
  }
  const data = await response.json();
  return {
    rows: {
      orderhistory: data.rows?.orderhistory ?? EMPTY_INSPECTION_ROWS.orderhistory,
      factuurOrders: data.rows?.factuurOrders ?? EMPTY_INSPECTION_ROWS.factuurOrders,
    },
    inventory: data.inventory ?? EMPTY_INVENTORY_DATA,
    costPrice: data.costPrice ?? EMPTY_COST_PRICE_DATA,
  };
}

/**
 * Fetch the extracted text of the original source file (PDF/CSV/text) of the email behind a
 * quantity-audit flag, addressed by its RFC822 message id (the flag's `gmailId`). `source` picks
 * which table's file is preferred (a factuur flag must read the factuur's PDF). Returns null when
 * nothing that email stored has a file. Feeds the raw invoice text into the code-monkey handoff.
 */
export async function fetchOrderSourceFileText(
  gmailId: string,
  source: 'pakbon' | 'factuur' = 'pakbon',
  setupSource?: SetupQuantitySource,
): Promise<{ fileText: string | null; sourceFileUrl: string | null }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/order-source-file-text`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ gmailId, source, setupSource }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch source file text: ${response.statusText}`);
  }
  const data = await response.json();
  return { fileText: data.fileText ?? null, sourceFileUrl: data.sourceFileUrl ?? null };
}

export interface OrderLookupProduct {
  name: string | null;
  articleId: string | null;
  quantity: number;
  rawQuantity: number | null;
  price: number;
  priceWithTaxes: number | null;
  category: string | null;
  quantityInfo: { weightString?: string; stdAmount?: number; stdMultiplier?: number; stdUnit?: string } | null;
}

/** A factuur_orders row linked to an order, plus its parent factuur_history invoice. */
export interface OrderLookupFactuur {
  factuurOrderId: string;
  factuurHistoryId: string;
  invoiceNumber: string;
  invoiceDate: string;
  orderNumber: string;
  orderDate: string;
  orderTotal: number | null;
  orderTotalWithTaxes: number | null;
  invoiceTotal: number;
  invoiceTotalWithTaxes: number | null;
  discount: number | null;
  shipping: number | null;
  messageId: string;
  gmailId: string | null;
  sourceFileUrl: string | null;
  productSum: number;
  products: OrderLookupProduct[];
}

export interface OrderLookupResult {
  id: string;
  orderNumber: string;
  platform: string;
  orderDate: string;
  total: number;
  totalWithTaxes: number | null;
  productSum: number;
  restaurantName: string;
  userEmail: string;
  userName: string | null;
  gmailId: string | null;
  sourceFileUrl: string | null;
  products: OrderLookupProduct[];
  /** Linked facturen (normally 0 or 1; empty when no factuur is linked to this order). */
  facturen: OrderLookupFactuur[];
}

/**
 * Look up orders by exact order_number across all restaurants.
 */
export async function lookupOrderByNumber(orderNumber: string): Promise<OrderLookupResult[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/order-lookup?orderNumber=${encodeURIComponent(orderNumber)}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to lookup order: ${response.statusText}`);
  }
  const data = await response.json();
  return data.results;
}

/** Args identifying a single flagged product to look up / recheck. */
export interface FlaggedProductRef {
  setupSource?: SetupQuantitySource;
  platform: string;
  productName: string;
  articleId?: string | null;
  /** Scope orders to this restaurant by name (the only reliable restaurant handle on AI-checker flags). */
  restaurantName?: string | null;
  gmailId?: string | null;
  /** Which table the flag came from — decides which lines the recheck re-audits. Default 'pakbon'. */
  source?: 'pakbon' | 'factuur';
  /**
   * The flag's own quantityInfo snapshot. Send it on a recheck: it lets the server tell "the data was
   * fixed" apart from "the AI happened not to flag it a second time" (`unchanged` in the result).
   */
  flaggedQuantityInfo?: { stdUnit?: string; stdAmount?: number; stdMultiplier?: number } | null;
}

/**
 * Fetch the most recent orders (one restaurant + platform) where a flagged product appeared,
 * for the quantity-audit detail modal.
 */
export async function getFlaggedProductOrders(ref: FlaggedProductRef): Promise<OrderLookupResult[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/flagged-product/orders`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(ref),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch product orders: ${response.statusText}`);
  }
  const data = await response.json();
  return data.orders;
}

export interface RecheckFlaggedProductResult {
  orders: OrderLookupResult[];
  flaggedProducts: FlaggedProduct[];
  stillFlagged: boolean;
  /** No line found in the flag's own table — nothing was audited, so this is NOT a clean result. */
  inconclusive?: boolean;
  /**
   * The stored quantityInfo is still EXACTLY what was flagged — the data was never fixed. A clean AI
   * verdict on unchanged data is a second opinion on the same judgement call, not proof, so callers
   * must never auto-close the flag while this is true.
   */
  unchanged?: boolean;
  aiCost: { inputTokens: number; outputTokens: number; totalCostEur: number };
}

/** Re-run the AI quantity audit on a flagged product's current data; reports whether it's still flagged. */
export async function recheckFlaggedProduct(ref: FlaggedProductRef): Promise<RecheckFlaggedProductResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/flagged-product/recheck`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(ref),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to recheck product: ${response.statusText}`);
  }
  return response.json();
}

export interface ProcessedEmailResult {
  gmailMessageId: string;
  rfc822MessageId: string | null;
  subject: string;
  from: string;
  date: string;
  matchedIn: 'order' | 'revenue';
  platform: string | null;
  restaurantName: string | null;
  orderNumbers: string[];
  storedTotal: number | null;
  verdict: 'correct' | 'wrong' | 'not_checked';
  issues: string[];
}

export interface NotProcessedEmailResult {
  gmailMessageId: string;
  rfc822MessageId: string | null;
  subject: string;
  from: string;
  date: string;
  verdict: 'good_skip' | 'needs_check';
  category: 'new_supplier' | 'known_supplier' | 'dedup' | 'informational' | 'emballage' | 'not_an_invoice' | 'known_skip' | 'no_products';
  reason: string;
  platform: string | null;
  source: 'deterministic' | 'ai';
}

export interface EmailAccuracyAuditResult {
  account: string;
  startDate: string;
  endDate: string;
  processed: {
    total: number;
    correctCount: number;
    wrongCount: number;
    notCheckedCount: number;
    emails: ProcessedEmailResult[];
  };
  notProcessed: {
    total: number;
    goodSkipCount: number;
    needsCheckCount: number;
    emails: NotProcessedEmailResult[];
  };
  aiCost: {
    sonnet: { inputTokens: number; outputTokens: number; totalCostEur: number };
    haiku: { inputTokens: number; outputTokens: number; totalCostEur: number };
    totalCostEur: number;
  };
}

/**
 * Unified email accuracy audit: for each email in range, correctness-check the processed ones and
 * classify the unprocessed ones (good-skip vs needs-check). Returns both sections + combined cost.
 */
export async function runEmailAccuracyAudit(
  account: string,
  startDate: string,
  endDate: string
): Promise<EmailAccuracyAuditResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/email-accuracy-audit`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run email accuracy audit: ${response.statusText}`);
  }
  return response.json();
}

// ── Continuous AI Checker (per-email audit traces) ──

export interface AICheckerStats {
  total: number;
  processed: number;
  processedCorrect: number;
  processedWrong: number;
  processedInconclusive: number;
  unprocessed: number;
  goodSkip: number;
  needsCheck: number;
  known: number;
  withQuantityFlags: number;
  quantityFlagTotal: number;
  withIssues: number;
  // Surfaced-issue breakdown (one primary type per email, independent of done/work_state).
  issueWrong: number;
  issueRevenue: number;
  issueFactuur: number;
  issueNeedsAttention: number;
  issueNewSupplier: number;
  issueQuantity: number;
  aiCostEur: number;
  processingCostEur: number;
}

export interface AICheckerAllTime {
  costEur: number;
  processingCostEur: number;
  checked: number;
  firstCheckedAt: string | null;
}

export interface AICheckerTrace {
  id: string;
  gmailMessageId: string;
  rfc822MessageId: string | null;
  account: string;
  emailSubject: string | null;
  emailFrom: string | null;
  emailDate: string | null;
  processed: boolean;
  matchedIn: 'order' | 'revenue' | null;
  /** Current email-owned orders/invoices, even if the last audit predates backprocessing. */
  hasStoredOrderData?: boolean;
  platform: string | null;
  restaurantName: string | null;
  orderNumbers: string[];
  storedTotal: number | null;
  emailVerdict: 'correct' | 'wrong' | 'inconclusive' | null;
  emailReasoning: string | null;
  emailIssues: string[];
  triageCategory: 'needs_attention' | 'new_supplier' | 'legit_skip' | 'known' | null;
  triageSkipKind: string | null;
  triageReason: string | null;
  triageSource: 'deterministic' | 'ai' | null;
  quantityFlagCount: number;
  quantityFlags: FlaggedProduct[];
  hasIssue: boolean;
  /** Super-admin overruled the checker and forced this email into the open issue queue. */
  manualIssue: boolean;
  workState: string;
  // Factuur verification (null = not applicable). Set for factuur-supported platforms.
  factuurExpected: boolean | null;
  factuurCreated: boolean | null;
  factuurOrdersCount: number | null;
  aiCostEur: number;
  processingCostEur: number;
  checkedAt: string;
  createdAt: string;
}

export interface AICheckerResult {
  account: string;
  startDate: string;
  endDate: string;
  stats: AICheckerStats;
  issues: AICheckerTrace[];
  allTime: AICheckerAllTime;
  quantityFlags: FlaggedProductWithRestaurant[];
}

/**
 * Read the stored continuous AI-Checker traces (score stats + open issue work items) for a mailbox
 * over a date range. Traces are written automatically after each email is processed.
 */
export async function getAICheckerData(
  account: string,
  startDate: string,
  endDate: string
): Promise<AICheckerResult> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ account, startDate, endDate });
  const response = await fetch(`${API_BASE}/ai-checker?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read AI checker data: ${response.statusText}`);
  }
  return response.json();
}

export interface AICheckerEmailsResult {
  account: string;
  startDate: string;
  endDate: string;
  total: number;
  emails: AICheckerTrace[];
}

/**
 * Full chronological feed of audited emails (most recent first) for a mailbox + date range.
 */
export async function getAICheckerEmails(
  account: string,
  startDate: string,
  endDate: string,
  limit = 200
): Promise<AICheckerEmailsResult> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ account, startDate, endDate, limit: String(limit) });
  const response = await fetch(`${API_BASE}/ai-checker/emails?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read AI checker emails: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch one audit-log trace by its row id — used to refresh a single row in the feed (e.g. after an
 * async reprocess) without reloading the whole list.
 */
export async function getAICheckerEmailById(id: string): Promise<AICheckerTrace> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/email/${encodeURIComponent(id)}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to refresh email row: ${response.statusText}`);
  }
  return response.json();
}

/** What a targeted reprocess achieved. `outcomes` carries one replay outcome per message found. */
export interface ReprocessEmailResult {
  found: number;
  processed: number;
  /** Read back from the DB after the replay — the ONLY proof "Add missing factuur" did anything. */
  factuurCreated: boolean;
  factuurOrdersCount: number;
  /** 'order_processed' | 'dedup_skip' | 'revenue' | 'no_platform_match' | … | 'error'; empty = not found. */
  outcomes: string[];
}

/**
 * Live-reprocess one targeted email (by its RFC822 Message-ID) in a specific mailbox, and refresh
 * its AI-Checker trace. Returns how many messages matched + were processed.
 * skipMessageIdDedup pushes through the already-processed guard — "Add missing factuur": the
 * orderhistory row exists (so normal reprocess would skip) but factuur_history was never written.
 *
 * A resolved promise means the REQUEST succeeded, never that data was written: the replay exits
 * silently when the sender no longer matches a restaurant_platform, when the email routes elsewhere,
 * or when the adapter emits no factuurData. Gate any success message on `factuurCreated`.
 */
export async function reprocessAICheckerEmail(
  account: string,
  rfc822MessageId: string,
  skipMessageIdDedup?: boolean
): Promise<ReprocessEmailResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/reprocess`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, rfc822MessageId, ...(skipMessageIdDedup ? { skipMessageIdDedup: true } : {}) }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to reprocess email: ${response.statusText}`);
  }
  return response.json();
}

/** One orderhistory row created by a backprocessed invoice PDF. */
export interface BackprocessOrder {
  orderNumber: string;
  clientName: string | null;
  restaurantName: string | null;
}

/** Outcome of a single invoice PDF inside a backprocessed email. */
export interface BackprocessFileResult {
  filename: string;
  ordersCreated: BackprocessOrder[];
  facturenCreated: number;
  error?: string;
}

export interface BackprocessResult {
  gmailMessageId: string;
  platform: string;
  pdfCount: number;
  totalOrders: number;
  totalFacturen: number;
  files: BackprocessFileResult[];
  /** quantityInfo flags found across everything the run stored. null when the audit could not run. */
  quantityFlagCount: number | null;
}

/**
 * Suppliers whose adapter supports bulk backprocessing — drives the platform dropdown. A few entries
 * are not platform ids but forced invoice layouts of one platform (e.g. 'sligro-heineken'); they are
 * posted back in the same `platform` field and resolved server-side.
 */
export async function getBackprocessPlatforms(): Promise<string[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/backprocess-platforms`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load backprocessing platforms: ${response.statusText}`);
  }
  const data = await response.json();
  return data.platforms ?? [];
}

/**
 * Bulk-process every invoice PDF in one email against the chosen supplier, regardless of the email's
 * subject/sender. Each invoice resolves its own restaurant by client-name matching. Safe to re-run.
 */
export async function backprocessAICheckerEmail(
  account: string,
  gmailMessageId: string,
  platform: string
): Promise<BackprocessResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/backprocess`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, gmailMessageId, platform }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to backprocess email: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Re-run ONLY the audit on one targeted email (by its RFC822 Message-ID), overwriting its AI-Checker
 * trace without re-running the platform handler. Use after a manual fix to re-evaluate the stored data.
 */
export async function reauditAICheckerEmail(
  account: string,
  rfc822MessageId: string
): Promise<{ found: number; audited: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/reaudit`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, rfc822MessageId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to reaudit email: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Narrows a quantity check to one restaurant's lines, optionally to some suppliers / categories.
 * Flags on the lines it skips are kept (see the server's mergeScopedQuantityFlags).
 */
export interface QuantityRecheckScope {
  restaurantId: string;
  platforms?: string[];
  mainCategories?: string[];
  categories?: string[];
}

export interface QuantityRecheckTarget {
  account: string;
  gmailMessageId: string;
  rfc822MessageId: string | null;
  subject: string | null;
  emailDate: string | null;
  platforms: string[];
  orderCount: number;
}

/** The audited emails holding a restaurant's orders in a date range — the scoped recheck work list. */
export async function fetchQuantityRecheckTargets(
  restaurantId: string,
  startDate: string,
  endDate: string,
  platforms: string[] = []
): Promise<{ targets: QuantityRecheckTarget[]; withoutAuditRow: number; restaurantPlatforms: string[] }> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ restaurantId, startDate, endDate });
  if (platforms.length) params.set('platforms', platforms.join(','));
  const response = await fetch(`${API_BASE}/ai-checker/quantity-recheck-targets?${params}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load recheck targets: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Re-run ONLY the quantityInfo audit for one already-processed email and store the flags on its
 * existing trace (verdict/triage untouched). Much cheaper than a full reaudit and makes no Gmail
 * call, so it is safe to loop over many rows. `updated` is false when the email has no order trace.
 */
export async function quantityCheckAICheckerEmail(
  account: string,
  gmailMessageId: string,
  rfc822MessageId?: string | null,
  scope?: QuantityRecheckScope
): Promise<{ updated: boolean; flagCount: number | null }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/quantity-check`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, gmailMessageId, rfc822MessageId: rfc822MessageId ?? undefined, ...scope }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run quantity check: ${response.statusText}`);
  }
  return response.json();
}

// --- Trivecgroup revenue check (dry-run + apply backfill) -----------------------------
// Scans historical trivecgroup report emails, diffs report ground-truth against config +
// stored revenue_history, and (apply mode) additively backfills fixable gaps in place.

/** A named section (subsection) of a restaurant block with its report value. */
export interface RevenueCheckSectionValue {
  name: string;
  value: number;
}

export interface RevenueCheckEmailFinding {
  rfc822MessageId: string;
  subject: string;
  reportDate: string; // YYYY-MM-DD as parsed from the report
  unconfiguredRestaurants: { reportName: string; totalValue: number }[];
  missingDays: { restaurantName: string; restaurantRevenueSourceId: string }[];
  missingSections: { restaurantName: string; revenueId: string; sections: RevenueCheckSectionValue[] }[];
  valueMismatches: { restaurantName: string; section: string; storedValue: number; reportValue: number; storedQuantity?: number; reportQuantity?: number }[];
  unconfiguredSections: { restaurantName: string; sections: RevenueCheckSectionValue[] }[]; // non-zero only
}

export interface RevenueCheckResult {
  mode: 'dry' | 'apply';
  /** Whether mismatched sections were actually written, or only reported. */
  overrideMismatches: boolean;
  emailsListed: number;
  emailsQualified: number;
  emailsFilteredOut: number;
  findings: RevenueCheckEmailFinding[];
  totals: {
    unconfiguredRestaurants: number;
    missingDays: number;
    rowsWithMissingSections: number;
    valueMismatches: number;
    unconfiguredSections: number;
  };
  // Cross-email rollup — the PRIMARY operator view. Each list sorted by totalValue
  // (or occurrences / dates length) descending.
  aggregate: {
    unconfiguredRestaurants: { reportName: string; days: number; totalValue: number }[];
    missingDays: { restaurantName: string; dates: string[] }[];
    missingSections: { restaurantName: string; section: string; dates: string[]; totalValue: number }[];
    valueMismatches: { restaurantName: string; section: string; occurrences: number; totalStored: number; totalReport: number; totalStoredQuantity?: number; totalReportQuantity?: number }[];
    unconfiguredSections: { restaurantName: string; section: string; days: number; totalValue: number }[];
  };
  applied?: {
    insertedDays: { restaurantName: string; date: string }[];
    mergedSections: { restaurantName: string; date: string; sections: string[] }[];
    overriddenSections: { restaurantName: string; date: string; sections: string[] }[];
    reauditedEmails: number;
  };
  errors: { rfc822MessageId?: string; subject?: string; error: string }[];
}

/**
 * Run the trivecgroup revenue check over the emails matching a raw Gmail search query.
 * `mode: 'dry'` only reports findings; `mode: 'apply'` additively backfills missing days /
 * configured sections in place (never overwrites or deletes) and re-audits touched emails.
 * `fileType: 'xlsx'` reads the "Sales Per Group" spreadsheets instead of the daily PDF report
 * (several day-files per email, no sender/subject filter) — for manually forwarded reports.
 * `overrideMismatches` additionally OVERWRITES stored sections whose total disagrees with the report.
 */
export async function runTrivecRevenueCheck(
  query: string,
  mode: 'dry' | 'apply',
  count?: number,
  fileType: 'pdf' | 'xlsx' | 'productmix-multiday' = 'pdf',
  overrideMismatches = false,
  account: 'cloud63' | 'cloud64' = 'cloud63'
): Promise<RevenueCheckResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/revenue-check`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, mode, count, fileType, overrideMismatches, account }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run revenue check: ${response.statusText}`);
  }
  return response.json();
}

// --- Bork direct API connector (trivecgroup revenue straight from the POS gateway) ----
// One connection per restaurant: the venue's own trivecgateway base URL plus the
// group-wide appid. The appid is write-only — the server hands back the last 4 chars only.

/** Mirrors `BorkConnection` in `server/domains/revenueData/sources/trivecgroup/borkConnector.ts`. */
export interface BorkConnection {
  /** The backing `revenue_automation_clients` row — the list key, not a connection id of its own. */
  clientId: string;
  name: string;
  restaurantId: string;
  restaurantName: string | null;
  baseUrl: string | null;
  /** Exact POS `CenterName` for multi-center venues; null = the venue's single center. */
  centerName: string | null;
  /**
   * Category name → revenue source type, for the categories this venue books somewhere other than
   * their POS group tree implies (Wapen van Willemstad / Bellevue: `Hotel`, `Verhuur`,
   * `Toeristenbelasting` → HOTEL). Empty when the venue has none.
   */
  sourceTypeOverrides: Record<string, string>;
  /** Already bullet-padded (`••••1234`); the plain appid never leaves the server. */
  appIdMasked: string | null;
  /** Included in the 07:00 daily automatic pull. */
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: 'success' | 'failure' | null;
  lastRunError: string | null;
  updatedAt: string;
}

export interface BorkConnectionInput {
  baseUrl: string;
  /** Omit (or leave blank) on edit to keep the stored appid. */
  appId?: string;
  centerName?: string | null;
  /** Omit to keep the stored map; `null` or `{}` clears it. */
  sourceTypeOverrides?: Record<string, string> | null;
  name?: string;
  /** Omitted on update preserves the stored flag; a newly created connection defaults to false. */
  isEnabled?: boolean;
}

/** One restaurant-day-source-row outcome of a Bork fetch, in every mode. */
export interface BorkDayResult {
  restaurantId: string;
  restaurantName: string;
  /** YYYY-MM-DD business day. */
  date: string;
  /** null when the venue fetch itself failed, so there is no per-source-row breakdown. */
  sourceType: string | null;
  status: 'exists' | 'missing' | 'no_section_match' | 'tunnel_down' | 'error';
  /** What the API says this source row is worth, after the level-2 group tree splits the day. */
  borkTotalInc: number;
  borkTotalEx: number;
  /** The whole API day incl VAT, before filtering. */
  dayTotalInc: number;
  /** Present when a revenue_history row already exists for this source row and day. */
  storedTotalInc?: number;
  storedTotalEx?: number;
  categories: { name: string; totalInc: number }[];
  /** Bork categories whose level-2 group we don't recognise. They still land in OTHER — an FYI
   *  flag, not lost revenue. */
  unmatchedCategories: string[];
  /** Category names this day's Bork data gains / loses against the stored row. Dry runs only, and
   *  only on stored days whose diff is non-empty. */
  categoriesAdded?: string[];
  categoriesRemoved?: string[];
  /** Set in apply/override mode when this run actually touched the row. `removed` is override
   *  deleting a stored row whose source type the API now reports empty — nothing replaces it.
   *  (A day-level guard means this never fires when the whole day's Bork revenue is zero.) */
  applied?: 'inserted' | 'replaced' | 'removed';
  error?: string;
}

/**
 * One restaurant's cost-price link check, computed by a dry run BEFORE anything is written — the
 * "am I safe to override" signal. Overriding replaces item names with the POS's own, so a linked
 * cost-price item whose name the pull no longer produces silently drops to zero sales.
 */
export interface BorkCostPriceCheck {
  restaurantId: string;
  restaurantName: string;
  /** Cost-price items currently linked to this restaurant's revenue item names. */
  linkedCount: number;
  /** Linked names the pull no longer produces — these are the ones an override would break. */
  breaking: string[];
  /** Names the pull produces that nothing was linked to before. */
  newlyLinked: string[];
}

export interface BorkFetchSummary {
  mode: 'dry' | 'apply' | 'override';
  startDate: string;
  endDate: string;
  venues: number;
  restaurantDays: number;
  inserted: number;
  replaced: number;
  /** Stored rows override DELETED without replacement — a destructive count, always surfaced. */
  removed: number;
  results: BorkDayResult[];
  /** Pre-write cost-price link check, one entry per restaurant in scope. Dry runs only. */
  costPriceChecks?: BorkCostPriceCheck[];
  /** Handle on the server-cached dry run, to be replayed by `applyBorkRun`. Dry runs only. */
  runId?: string;
}

/** One restaurant-day picked out of a dry run — the unit of work `applyBorkRun` takes. */
export interface BorkDaySelection {
  restaurantId: string;
  date: string;
}

export async function listBorkConnections(): Promise<BorkConnection[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/bork/connections`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load Bork connections: ${response.statusText}`);
  }
  return response.json();
}

/** Create or update the connection of one restaurant (upsert on restaurantId). */
export async function saveBorkConnection(restaurantId: string, input: BorkConnectionInput): Promise<BorkConnection> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/bork/connections/${restaurantId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to save Bork connection: ${response.statusText}`);
  }
  return response.json();
}

/** Deletes only the API credentials row; stored revenue history is untouched. Answers 204, no body. */
export async function deleteBorkConnection(restaurantId: string): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/bork/connections/${restaurantId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete Bork connection: ${response.statusText}`);
  }
}

/** One restaurant-day finished on the server, as the dry-run stream reports it. */
export interface BorkFetchProgress {
  completedDays: number;
  totalDays: number;
  restaurantName: string;
  date: string;
  status: BorkDayResult['status'];
}

/**
 * The server sent an explicit `error` frame — the run failed for a stated reason and is NOT coming
 * back. Distinct from a dropped socket, which leaves the run alive server-side: the message here is
 * the actionable one (e.g. the range exceeds the restaurant-day cap and has to be split), so it must
 * reach the operator rather than being mistaken for a blip and quietly polled.
 */
export class BorkStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BorkStreamError';
  }
}

/** First event of the stream — the run exists server-side from here on, drop or no drop. */
export interface BorkRunStarted {
  runId: string;
  totalDays: number;
}

/** Live callbacks for the streaming dry run — each fires as the backend emits it. */
export interface BorkFetchStreamHandlers {
  /** Fires before any progress. Hold this `runId`: it is what recovers the run if the socket dies. */
  onStarted?: (started: BorkRunStarted) => void;
  onProgress?: (progress: BorkFetchProgress) => void;
  /** The completed dry run, carrying the `runId` that `applyBorkRun` replays. Last event of the stream. */
  onSummary?: (summary: BorkFetchSummary) => void;
}

/**
 * Dry-run the Bork pull over a date range, streaming per-day progress (SSE over a POST fetch —
 * keeps the Bearer token in the header, unlike `EventSource`). Writes nothing: it only compares
 * the API day totals against stored `revenue_history` and caches the result server-side under a
 * `runId` that `applyBorkRun` can then write from. Resolves when the stream ends; rejects on a
 * transport error or a server-sent `error` event. Pass `signal` to cancel an in-flight stream.
 */
export async function runBorkFetchStream(
  params: { startDate: string; endDate: string; restaurantIds?: string[] },
  handlers: BorkFetchStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/bork/fetch/stream`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    signal,
  });
  if (!response.ok || !response.body) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run Bork fetch: ${response.statusText}`);
  }

  await readSseStream(response.body, ({ event, payload }) => {
    // Frames are discriminated by `data.type`. The shared emitter also names the frame, so the
    // `event:` name stands in if a payload ever arrives without its discriminator.
    switch ((payload.type as string) || event) {
      case 'started':
        handlers.onStarted?.(payload as unknown as BorkRunStarted);
        break;
      case 'progress':
        handlers.onProgress?.(payload as unknown as BorkFetchProgress);
        break;
      case 'summary':
        handlers.onSummary?.(payload.summary as BorkFetchSummary);
        break;
      case 'error':
        throw new BorkStreamError((payload.message as string) || (payload.error as string) || 'Bork fetch stream error');
      default:
        break;
    }
  });
}

/** The cached dry run behind a `runId` is gone (TTL or a server restart) — it has to be re-run. */
export class BorkRunExpiredError extends Error {
  constructor() {
    super('bork_run_expired');
    this.name = 'BorkRunExpiredError';
  }
}

/**
 * The dry run behind this `runId` died mid-fetch. Terminal like an expired run and fixed the same
 * way — a fresh dry run — but a different cause, so the operator is told which happened.
 */
export class BorkRunFailedError extends Error {
  constructor() {
    super('bork_run_failed');
    this.name = 'BorkRunFailedError';
  }
}

/** The dry run behind this `runId` has not finished — apply has nothing complete to write yet. */
export class BorkRunStillRunningError extends Error {
  constructor() {
    super('bork_run_still_running');
    this.name = 'BorkRunStillRunningError';
  }
}

/**
 * Where a server-side run has got to. `complete` carries the same summary the stream would have;
 * `failed` is a run killed by an unexpected mid-run exception, which is terminal — without it a
 * poller would wait forever on a run that is never going to report progress again.
 */
export type BorkRunStatus =
  | { status: 'running'; completedDays: number; totalDays: number }
  | { status: 'complete'; summary: BorkFetchSummary }
  | { status: 'failed'; error: string };

/**
 * Poll a run the stream stopped reporting on. The dry run outlives its socket, so a dropped
 * connection is recoverable: keep asking until `complete` and the result is the one the stream
 * would have delivered. Throws `BorkRunExpiredError` once the server has dropped the run.
 */
export async function getBorkRunStatus(runId: string): Promise<BorkRunStatus> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/bork/runs/${runId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (response.status === 410) throw new BorkRunExpiredError();
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read Bork run status: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Write a reviewed dry run to `revenue_history`. `apply` writes ONLY the picked days that have
 * no stored row; `override` deletes the stored rows for those days first and replaces them with
 * the Bork data. Omitting `days` applies the whole run. Throws `BorkRunExpiredError` when the
 * cached dry run no longer exists.
 */
export async function applyBorkRun(params: {
  runId: string;
  mode: 'apply' | 'override';
  days?: BorkDaySelection[];
}): Promise<BorkFetchSummary> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/bork/apply`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    // Both 410s and one of the 409s are terminal and want the same remedy — a fresh dry run — but
    // they are told apart by BODY, not status, so each states its own cause:
    //   410 expired  — the cache dropped the run
    //   410 failed   — the run died mid-fetch
    //   409 still_running — registered but never finished (crashed-run edge case)
    //   409 anything else — the in-flight guard, which carries its own message and IS retryable
    if (response.status === 410) {
      throw err.error === 'failed' ? new BorkRunFailedError() : new BorkRunExpiredError();
    }
    if (response.status === 409 && err.error === 'still_running') throw new BorkRunStillRunningError();
    throw new Error(err.error || `Failed to apply Bork run: ${response.statusText}`);
  }
  return response.json();
}

// --- Van Gelder pakbon replay (flip-time backfill for vangelderPakbonMode) ------------
// Reruns the pakbon emails of a window through the normal pipeline so the standing
// supersede rule converts CS-numbered rows to long-number rows. Dry run predicts
// without writing and costs no AI.

export type VangelderReplayPrediction = 'supersede' | 'insert' | 'skip' | 'unresolvable' | 'unknown';

export interface VangelderReplayEmail {
  account: string;
  gmailMessageId: string;
  rfc822MessageId: string;
  subject: string;
  emailDate: string | null;
  clientCode: string | null;
  orderNumberText: string | null;
  longOrderNumber: string | null;
  csOrderNumber: string | null;
  targets: {
    restaurantPlatformId: string;
    pakbonMode: boolean;
    /** "Restaurant · Section" — only present for the all-ON sweep. */
    label?: string;
    prediction: VangelderReplayPrediction;
    storedAfter?: boolean;
  }[];
  skippedReason?: 'no_pakbon_pdf' | 'no_client_code' | 'client_not_matched';
  replayed: boolean;
  error?: string;
}

export interface VangelderReplayResult {
  dryRun: boolean;
  fromDate: string;
  toDate: string;
  gmailQuery: string;
  accountsScanned: string[];
  targetPlatforms: { restaurantPlatformId: string; pakbonMode: boolean; orderClientNames: string[] | null; label?: string }[];
  emailsListed: number;
  /** A mailbox returned exactly maxEmails ids — the window is probably cut short. */
  listingTruncated: boolean;
  emailsInspected: number;
  emailsMatched: number;
  emailsReplayed: number;
  counts: {
    supersede: number;
    insert: number;
    skip: number;
    unresolvable: number;
    unknown: number;
    noPakbonPdf: number;
    noClientCode: number;
    clientNotMatched: number;
    errors: number;
  };
  emails: VangelderReplayEmail[];
}

/**
 * Run the Van Gelder pakbon replay for one restaurant, one restaurant_platform, or
 * (`allPakbonModePlatforms`) every non-demo vangelder connection whose pakbon mode is ON.
 * `dryRun: true` scans Gmail and predicts per email (supersede/insert/skip/unresolvable)
 * without writing anything; `dryRun: false` replays the matched emails for real.
 */
export async function runVangelderPakbonReplay(params: {
  restaurantId?: string;
  restaurantPlatformId?: string;
  allPakbonModePlatforms?: boolean;
  fromDate: string;
  toDate: string;
  dryRun: boolean;
  account?: 'cloud63' | 'cloud64';
  maxEmails?: number;
}): Promise<VangelderReplayResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/vangelder-pakbon-replay`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run vangelder pakbon replay: ${response.statusText}`);
  }
  return response.json();
}

export type VangelderRzReplayFileStatus = 'preview' | 'no_rz' | 'unreadable' | 'created' | 'already_stored' | 'nothing_stored';

export interface VangelderRzReplayResult {
  dryRun: boolean;
  emails: {
    gmailMessageId: string;
    error?: string;
    files: {
      filename: string;
      status: VangelderRzReplayFileStatus;
      invoiceNumber?: string;
      rzInvoiceNumber?: string;
      clientName?: string;
      sections: number;
      rzTotal: number;
      ordersCreated: number;
      facturenCreated: number;
    }[];
  }[];
}

/** Stores only the Van Gelder sections of received Bidfood facturen; `dryRun` writes nothing. */
export async function runVangelderRzReplay(params: {
  account: 'cloud63' | 'cloud64';
  gmailMessageIds: string[];
  dryRun: boolean;
}): Promise<VangelderRzReplayResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/vangelder-rz-replay`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to run Van Gelder replay: ${response.statusText}`);
  }
  return response.json();
}

export interface FactuurTotalsCheck {
  verdict: 'correct' | 'wrong' | 'inconclusive';
  reasoning: string;
  /**
   * Which document was reconciled. 'factuur' (default) compares incl-VAT grand totals; 'pakbon'
   * compares ex-VAT (dzw/bidfood delivery notes print no VAT at all) and covers only this email's own
   * raw rows. The UI labels and reads the two differently.
   */
  documentType?: 'factuur' | 'pakbon';
  orderCount: number;
  ourTotalExVat: number;
  ourTotalInclVat: number;
  invoiceTotalExVat: number | null;
  invoiceTotalInclVat: number | null;
  aiCost: { inputTokens: number; outputTokens: number; totalCostEur: number };
}

/** One attached invoice PDF's completeness result within a backprocessing deep check. */
export interface BackprocessingFileCheck {
  filename: string;
  referenced: string[];
  stored: string[];
  missing: string[];
}

/** One attached invoice PDF's result within a mixed-suppliers deep check (files from DIFFERENT suppliers). */
export interface MixedSupplierFileCheck {
  filename: string;
  /** Platform detected from the invoice text; null when no known identifier matched. */
  platform: string | null;
  /** Supplier name as printed on the invoice (AI-extracted) — identification when platform is null. */
  supplierName: string | null;
  referenced: string[];
  /** Referenced numbers found in DB, with what they matched (orderhistory row or factuur). */
  matches: { number: string; source: 'order' | 'factuur'; platform: string; restaurantName: string }[];
  missing: string[];
  /** true when at least one referenced number is stored — the file made it into the DB. */
  found: boolean;
}

export interface ConsolidatedDeepCheckResult {
  ok: boolean;
  reason?: string;
  referenced: string[];
  stored: string[];
  missing: string[];
  aiCost: { inputTokens: number; outputTokens: number; totalCostEur: number };
  /** Present when the number check escalated to a totals reconciliation (split-storage factuurs). */
  totals?: FactuurTotalsCheck;
  /** Present ONLY for backprocessing emails — per-attachment completeness breakdown. */
  files?: BackprocessingFileCheck[];
  /** Present ONLY for the mixed-suppliers deep check — per-attachment found/not-found breakdown. */
  mixedFiles?: MixedSupplierFileCheck[];
}

/**
 * Consolidated-factuur completeness check on one email (by RFC822 Message-ID): extracts every
 * referenced pakbon/order number and confirms each is stored under the same restaurant+platform.
 * Read-only — does not mutate orders or the audit row.
 *
 * `mode` defaults to 'auto' (the subject decides which check runs); 'backprocessing' forces the
 * per-file check, for an email backprocessed manually from the AI Checker; 'mixed' runs the per-file
 * check for an email whose PDFs come from different suppliers (platform detected per file); 'totals'
 * skips the number check and reconciles the sum of all stored rows against the factuur grand totals;
 * 'pakbon' is the delivery-note counterpart of 'totals' (this email's own raw rows vs the pakbon's
 * printed totals) — for pakbon-only suppliers such as gnb, dzw and bidfood.
 */
export type DeepCheckMode = 'auto' | 'backprocessing' | 'mixed' | 'totals' | 'pakbon';

export async function deepCheckAICheckerEmail(
  account: string,
  rfc822MessageId: string,
  mode: DeepCheckMode = 'auto'
): Promise<ConsolidatedDeepCheckResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/deep-check`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, rfc822MessageId, mode }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to deep-check email: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Backfill audit-log rows for an entire historical date range on one mailbox (audit-only — does NOT
 * re-run the platform handler, so stored orders/revenue are untouched). Returns the count of emails
 * queued; the audit runs in the background and rows appear in the feed as they complete.
 */
export async function backfillAICheckerRange(
  account: string,
  startDate: string,
  endDate: string
): Promise<{ toBackfill: number; skipped: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/backfill`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to backfill audit: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Process-AND-audit backfill for an entire historical date range on one mailbox: emails without an
 * audit-log row get the full notify treatment (processing recovers missed orders/revenue/facturen,
 * then the audit runs); emails with an audit row are skipped entirely. Idempotent; price-alert emails
 * to clients are suppressed. Returns the counts immediately — the run continues in the background.
 */
export async function processBackfillAICheckerRange(
  account: string,
  startDate: string,
  endDate: string
): Promise<{ toProcess: number; skipped: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/process-backfill`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to start process-backfill: ${response.statusText}`);
  }
  return response.json();
}

/**
 * One-time fill of email_candidates on rows audited before candidate capture shipped: re-fetches only
 * the message headers (no body, no AI) and extracts the addresses cloud-system attributes on. Selects
 * ONLY rows missing candidates and UPDATEs only that column — nothing is reprocessed or re-audited, so
 * it's safe on 'all' and idempotent (already-filled rows are skipped). Runs in the background.
 */
export async function backfillAICheckerCandidates(
  account: string,
  startDate: string,
  endDate: string
): Promise<{ toBackfill: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/backfill-candidates`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to start candidate backfill: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete the stored order(s) created from one email (by its Gmail message id). Does not change the
 * audit row — re-audit afterwards to refresh the trace. Returns how many orders were deleted.
 */
export async function deleteAICheckerOrder(gmailMessageId: string): Promise<{ deleted: number; facturenDeleted: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/delete-order`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ gmailMessageId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete order: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete the stored revenue entries created from one email (by its Gmail message id). Does not change
 * the audit row — re-audit afterwards to refresh the trace. Returns how many rows were deleted.
 */
export async function deleteAICheckerRevenue(gmailMessageId: string): Promise<{ deleted: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/delete-revenue`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ gmailMessageId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete revenue: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Mark an audited email done/open (burn-down). Resolved rows drop out of the issue buckets.
 */
export async function setAICheckerWorkState(
  id: string,
  workState: 'open' | 'resolved' | 'dismissed'
): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/work-state`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, workState }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update work state: ${response.statusText}`);
  }
}

/**
 * Force one audited email into the open issue queue by hand (or lift that mark again) — the override
 * for an email the checker triaged as a safe skip that is actually a problem. Only the manual flag
 * moves; the checker's own triage/verdict stay intact, so lifting it restores its conclusion.
 */
export async function setAICheckerManualIssue(id: string, manual: boolean): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/mark-issue`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, manual }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update manual issue: ${response.statusText}`);
  }
}

/**
 * Mark one quantity-info flag done/open in place (burn-down for quantity flags).
 */
export async function resolveAICheckerQuantityFlag(
  traceId: string,
  articleId: string,
  productName: string,
  source: 'pakbon' | 'factuur',
  resolved = true,
  options?: { fromRecheck?: boolean }
): Promise<{ fileVerified: boolean }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/quantity-flag`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ traceId, articleId, productName, source, resolved, ...(options?.fromRecheck ? { fromRecheck: true } : {}) }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update quantity flag: ${response.statusText}`);
  }
  const data = await response.json().catch(() => ({}));
  return { fileVerified: data.fileVerified === true };
}

/**
 * Mark MANY quantity-info flags done/open in one call — the per-supplier "mark all as done". Send the
 * exact flags being shown so nothing outside the current view gets touched.
 */
export async function resolveAICheckerQuantityFlagsBulk(
  flags: Array<{ traceId: string; articleId: string; productName: string; source: 'pakbon' | 'factuur' }>,
  resolved = true
): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/quantity-flags-bulk`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ flags, resolved }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to bulk-update quantity flags: ${response.statusText}`);
  }
}

/**
 * Read the quantity-info flags already marked done in a range — used by the "show done" toggle so an
 * operator can review and undo accidental "done" clicks.
 */
export async function getAICheckerResolvedQuantityFlags(
  account: string,
  startDate: string,
  endDate: string
): Promise<FlaggedProductWithRestaurant[]> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ account, startDate, endDate });
  const response = await fetch(`${API_BASE}/ai-checker/quantity-flags-resolved?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read resolved quantity flags: ${response.statusText}`);
  }
  const data = await response.json();
  return data.quantityFlags ?? [];
}

/**
 * Read the quantity-info flags filed as known to-dos in a range (with their `knownCategory`) — used by
 * the "show known" toggle so an operator can review and undo a mis-filed flag back to open.
 */
export async function getAICheckerKnownQuantityFlags(
  account: string,
  startDate: string,
  endDate: string
): Promise<FlaggedProductWithRestaurant[]> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ account, startDate, endDate });
  const response = await fetch(`${API_BASE}/ai-checker/quantity-flags-known?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read known quantity flags: ${response.statusText}`);
  }
  const data = await response.json();
  return data.quantityFlags ?? [];
}

/** Mirrors FlaggedProduct.quantityInfo — a standardized quantity read-out for one line. */
export interface QuantityInfo {
  weightString: string;
  stdAmount: number;
  stdMultiplier: number;
  stdUnit: string;
}

/** One pakbon↔factuur quantity mismatch, grouped by (platform, matchKey/articleId, product). */
export interface MismatchGroup {
  platform: string;
  matchKey: string;
  articleId: string;
  productName: string;
  pakbonQuantityInfo: QuantityInfo;
  factuurQuantityInfo: QuantityInfo;
  occurrences: number;
  restaurantNames: string[];
  exampleOrderNumber: string;
  exampleOrderDate: string;
}

export interface QuantityMismatchesResult {
  groups: MismatchGroup[];
  totalOccurrences: number;
  platformCounts: Record<string, number>;
}

/**
 * Read pakbon/factuur quantity mismatches in a range (not per-mailbox). Surfaces which platforms need
 * the factuur-enrichment fix. Grouped server-side; name-keyed for platforms without article ids.
 */
export async function getAICheckerQuantityMismatches(
  startDate: string,
  endDate: string
): Promise<QuantityMismatchesResult> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ startDate, endDate });
  const response = await fetch(`${API_BASE}/ai-checker/quantity-mismatches?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read quantity mismatches: ${response.statusText}`);
  }
  const data = await response.json();
  return {
    groups: data.groups ?? [],
    totalOccurrences: data.totalOccurrences ?? 0,
    platformCounts: data.platformCounts ?? {},
  };
}

/** One factuur order with no linked orderhistory row (always a bug to investigate). */
export interface UnlinkedFactuurOrder {
  factuurOrderId: string;
  factuurHistoryId: string;
  invoiceNumber: string;
  platform: string;
  restaurantName: string;
  restaurantPlatformId: string;
  orderNumber: string;
  orderDate: string;
  total: number | null;
  productCount: number;
  sourceFileUrl: string | null;
  status: 'relinkable' | 'order_missing';
}

export interface UnlinkedFactuurOrdersResult {
  orders: UnlinkedFactuurOrder[];
}

/**
 * Read factuur orders that failed to link to an orderhistory row in a range. A non-empty list is
 * always a bug (delete-order + reprocess can orphan factuur_orders — manual repair for now).
 */
export async function getAICheckerUnlinkedFactuurOrders(
  startDate: string,
  endDate: string
): Promise<UnlinkedFactuurOrder[]> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ startDate, endDate });
  const response = await fetch(`${API_BASE}/ai-checker/unlinked-factuur-orders?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read unlinked factuur orders: ${response.statusText}`);
  }
  const data = await response.json();
  return data.orders ?? [];
}

/** One product line present on exactly one side of a same-file pakbon/factuur pair. */
export interface DivergentLine {
  matchKey: string;
  articleId: string;
  productName: string;
  quantity: number;
  price: number;
  value: number;
}

/**
 * One same-file pair whose line sets disagree. Same-file only (shared message_id), so the two sides
 * were built from the same document and MUST match — any divergence means a double extraction.
 */
export interface LineSetDivergence {
  platform: string;
  restaurantName: string;
  invoiceNumber: string;
  orderNumber: string;
  orderDate: string;
  orderHistoryId: string;
  factuurOrderId: string;
  pakbonLineCount: number;
  factuurLineCount: number;
  pakbonTotal: number | null;
  factuurTotal: number | null;
  valueDelta: number;
  onlyInPakbon: DivergentLine[];
  onlyInFactuur: DivergentLine[];
}

export interface LineSetDivergencesResult {
  divergences: LineSetDivergence[];
  totalDivergingLines: number;
  platformCounts: Record<string, number>;
}

/**
 * Read pakbon/factuur line-set divergences in a range (not per-mailbox). Only pairs written from the
 * SAME source file are examined — when pakbon and factuur are two different files, differing lines
 * are normal.
 */
export async function getAICheckerLineSetDivergences(
  startDate: string,
  endDate: string
): Promise<LineSetDivergencesResult> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ startDate, endDate });
  const response = await fetch(`${API_BASE}/ai-checker/line-set-divergences?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read line-set divergences: ${response.statusText}`);
  }
  const data = await response.json();
  return {
    divergences: data.divergences ?? [],
    totalDivergingLines: data.totalDivergingLines ?? 0,
    platformCounts: data.platformCounts ?? {},
  };
}

/** Per-supplier source-file coverage of the stored orders/facturen in a window. */
export interface MissingSourceFileRow {
  platform: string;
  totalOrders: number;
  missingOrders: number;
  missingEmails: number;
  missingFacturen: number;
}

export interface MissingSourceFileClientRow {
  userId: string;
  clientName: string;
  missingOrders: number;
  missingEmails: number;
}

export interface MissingSourceFileStats {
  startDate: string;
  endDate: string;
  totalOrders: number;
  missingOrders: number;
  missingEmails: number;
  totalFacturen: number;
  missingFacturen: number;
  byPlatform: MissingSourceFileRow[];
  byClient: MissingSourceFileClientRow[];
}

export interface AttachMissingFilesEmail {
  gmailMessageId: string;
  account: string;
  platform: string;
  pdfCount: number;
  ordersLinked: number;
  facturenLinked: number;
  outcome: 'linked' | 'no_pdf_attachment' | 'no_match_in_attachments' | 'email_not_found' | 'upload_failed';
  unmatchedOrderNumbers: string[];
  error?: string;
}

export interface AttachMissingFilesResult {
  dryRun: boolean;
  emailsConsidered: number;
  emailsProcessed: number;
  ordersLinked: number;
  facturenLinked: number;
  emails: AttachMissingFilesEmail[];
}

/** One affected email in the drill-down under a supplier. */
export interface MissingSourceFileEmail {
  gmailMessageId: string;
  account: string | null;
  platform: string;
  subject: string | null;
  emailDate: string | null;
  /** ALL fileless orders this email wrote — the true repair size (not date-scoped). */
  missingOrders: number;
  /** The subset inside the selected window — reconciles with the per-supplier table. */
  missingOrdersInWindow: number;
  distinctOrders: number;
  shape: 'single' | 'multi' | 'bundle';
  filesAlreadyOnEmail: number;
}

/** The individual emails behind one supplier's missing files, worst first. */
export async function getAICheckerMissingFileEmails(
  startDate: string,
  endDate: string,
  platform: string,
  limit: number = 100,
  userId?: string
): Promise<MissingSourceFileEmail[]> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ startDate, endDate, platform, limit: String(limit) });
  if (userId) params.set('userId', userId);
  const response = await fetch(`${API_BASE}/ai-checker/missing-files/emails?${params.toString()}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to list affected emails: ${response.statusText}`);
  }
  const data = await response.json();
  return data.emails ?? [];
}

/** How many stored orders/facturen in the window have no source file, per supplier. Read-only. */
export async function getAICheckerMissingFiles(
  startDate: string,
  endDate: string,
  platform: string = 'all',
  userId?: string
): Promise<MissingSourceFileStats> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ startDate, endDate, platform });
  if (userId) params.set('userId', userId);
  const response = await fetch(`${API_BASE}/ai-checker/missing-files?${params.toString()}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load missing source files: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Re-fetch the emails behind fileless orders/facturen and link each row to the attachment that
 * contains it. Runs one batch of `emailLimit` emails at a time and is idempotent, so repeating the
 * call walks through a large gap. `dryRun` reports what would be linked without uploading anything.
 */
export async function attachAICheckerMissingFiles(params: {
  account: string;
  startDate: string;
  endDate: string;
  platform?: string;
  /** Scope the run to one client (admin users.id). Omitted → every client. */
  userId?: string;
  emailLimit?: number;
  dryRun?: boolean;
  /** Repair exactly these emails and ignore the window — the per-email action. */
  gmailMessageIds?: string[];
}): Promise<AttachMissingFilesResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/attach-missing-files`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to attach missing files: ${response.statusText}`);
  }
  return response.json();
}

/** User-managed AI-Checker safe-skip lists (extend the code-defined good-skip lists). */
export interface AICheckerSafeSkips {
  senders: string[];
  subjects: string[];
}

/** Read the user-managed safe-skip lists (senders + subjects). */
export async function getAICheckerSafeSkips(): Promise<AICheckerSafeSkips> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/safe-skips`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read safe-skips: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Add or remove one safe-skip entry; returns the updated lists. On 'add' the server also
 * retroactively flips matching stored audit rows to legit_skip — `retroCount` reports how many.
 */
export async function updateAICheckerSafeSkip(
  type: 'sender' | 'subject',
  action: 'add' | 'remove',
  value: string
): Promise<AICheckerSafeSkips & { retroCount?: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/safe-skips`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ type, action, value }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update safe-skips: ${response.statusText}`);
  }
  return response.json();
}

/** Outcome of an AI-free safe-skip recheck over a period. */
export interface AICheckerSkipRecheckResult {
  checked: number;
  headerMatched: number;
  bodyMatched: number;
  updated: number;
  fetchErrors: number;
}

/**
 * Re-apply all current safe-skip rules (code + user lists) to the period's needs-check emails —
 * deterministic only (no AI); refetches email bodies so forwarded original senders match too.
 */
export async function recheckAICheckerSafeSkips(
  account: string,
  startDate: string,
  endDate: string
): Promise<AICheckerSkipRecheckResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/recheck-skips`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to recheck safe skips: ${response.statusText}`);
  }
  return response.json();
}

/** Product lines with no `mainCategory`, per platform, across orderhistory + factuur_orders. */
export interface AICheckerMissingCategorizationsStats {
  platforms: {
    platformName: string;
    orderhistoryLines: number;
    factuurLines: number;
    distinctNames: string[];
  }[];
  totalLines: number;
}

/** Outcome of a missing-categorization backfill run (AI only for genuinely new product names). */
export interface AICheckerMissingCategorizationsFixResult {
  dryRun: boolean;
  processed: number;
  updatedOrderhistoryRows: number;
  updatedFactuurRows: number;
  byPlatform: { platformName: string; lines: number }[];
}

/** Read-only count of unclassified product lines. Cheap — no AI, no writes. */
export async function fetchAICheckerMissingCategorizations(): Promise<AICheckerMissingCategorizationsStats> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/missing-categorizations`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load missing categorizations: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Classify the product lines that have no `mainCategory` and write the result back into BOTH
 * orderhistory and factuur_orders. Idempotent: only lines missing the field are touched.
 */
export async function fixAICheckerMissingCategorizations(
  options: { dryRun?: boolean; limit?: number } = {}
): Promise<AICheckerMissingCategorizationsFixResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/fix-missing-categorizations`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fix missing categorizations: ${response.statusText}`);
  }
  return response.json();
}

/** Outcome of an AI-free archive-attachment recheck over a period. */
export interface AICheckerArchiveRecheckResult {
  checked: number;
  gmailHits: number;
  flagged: number;
  fetchErrors: number;
}

/**
 * Re-scan the period's safe-skipped emails for archive attachments (.zip/.rar/…) — nothing in the
 * pipeline opens an archive, so those get flipped to "should have processed". Deterministic (no AI).
 */
export async function recheckAICheckerArchives(
  account: string,
  startDate: string,
  endDate: string
): Promise<AICheckerArchiveRecheckResult> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/recheck-archives`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account, startDate, endDate }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to recheck archives: ${response.statusText}`);
  }
  return response.json();
}

/** One known email line item filed under a to-do category (the actual audit row, linked at mark time). */
export interface AICheckerKnownEmail {
  id: string;
  gmailMessageId: string | null;
  from: string | null;
  subject: string | null;
  date: string | null;
  account: string | null;
}

/** One known quantity-info flag line item filed under a to-do category. */
export interface AICheckerKnownQtyFlag {
  traceId: string;
  restaurantName: string;
  productName: string;
  articleId: string;
  reason: string;
}

/** One to-do category: how many known emails + quantity flags fall under it, plus the line items. */
export interface AICheckerKnownTodo {
  category: string;
  emailCount: number;
  qtyCount: number;
  total: number;
  emails: AICheckerKnownEmail[];
  qtyFlags: AICheckerKnownQtyFlag[];
}

/** Known to-dos grouped by category, plus the reusable category list (for the picker). */
export interface AICheckerKnownCases {
  categories: string[];
  todos: AICheckerKnownTodo[];
  total: number;
}

/**
 * Fetch extracted invoice text (pdftotext output) for a known-case's linked emails — used to embed real
 * example invoice texts in the "Add Supplier" code-monkey prompt. Deduped + capped server-side.
 */
export async function getKnownCaseInvoiceTexts(
  emails: Array<{ account: string; gmailMessageId: string }>,
): Promise<Array<{ gmailMessageId: string; text: string }>> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/known-case-invoice-texts`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ emails }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch invoice texts: ${response.statusText}`);
  }
  const data = await response.json();
  return Array.isArray(data.texts) ? data.texts : [];
}

/**
 * Fetch the ORIGINAL PDF attachments (filename + base64) of a known-case's linked emails — sibling of
 * getKnownCaseInvoiceTexts, used by the "Add Supplier" code-monkey hand-off so the task folder carries the
 * real invoice files (logo extraction / dominant-color derivation need the PDF, not just its text).
 */
export async function getKnownCaseInvoiceFiles(
  emails: Array<{ account: string; gmailMessageId: string }>,
): Promise<Array<{ gmailMessageId: string; filename: string; dataBase64: string }>> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/known-case-invoice-files`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ emails }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch invoice files: ${response.statusText}`);
  }
  const data = await response.json();
  return Array.isArray(data.files) ? data.files : [];
}

/** Read the known to-dos (grouped by category, with counts) for a mailbox + date range. */
export async function getAICheckerKnownCases(
  account: string,
  startDate: string,
  endDate: string
): Promise<AICheckerKnownCases> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ account, startDate, endDate });
  const response = await fetch(`${API_BASE}/ai-checker/known-cases?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read known-cases: ${response.statusText}`);
  }
  return response.json();
}

/** Read the reusable to-do categories (for the "pick existing or type new" picker). */
export async function getAICheckerKnownCategories(): Promise<string[]> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/known-categories`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read known-categories: ${response.statusText}`);
  }
  const data = await response.json();
  return Array.isArray(data?.categories) ? data.categories : [];
}

/** Mark one audited email as a known case under a to-do category (flips ONLY this row out of the queue). */
export async function markAICheckerEmailKnown(id: string, category: string): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/mark-known`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, category }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to mark email known: ${response.statusText}`);
  }
}

/** Un-mark one audited email that was filed as a known to-do — flips it back into the open queue. */
export async function unmarkAICheckerEmailKnown(id: string): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/unmark-known`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to un-mark email known: ${response.statusText}`);
  }
}

/**
 * Delete a whole known to-do once it's handled/deployed: removes the category's patterns + label and
 * un-marks every linked email & quantity flag (across all accounts/dates) so they re-open as issues.
 * Returns how many emails / quantity flags were re-opened.
 */
export async function deleteAICheckerKnownCategory(
  category: string
): Promise<{ emailsReopened: number; qtyFlagsReopened: number }> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/delete-known-category`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ category }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete known to-do: ${response.statusText}`);
  }
  const data = await response.json().catch(() => ({}));
  return { emailsReopened: data.emailsReopened ?? 0, qtyFlagsReopened: data.qtyFlagsReopened ?? 0 };
}

/** Mark one quantity-info flag as a known to-do under a category (or undo with known=false). */
export async function setAICheckerQuantityFlagKnown(
  traceId: string,
  articleId: string,
  productName: string,
  category: string,
  source: 'pakbon' | 'factuur',
  known = true
): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/ai-checker/quantity-flag-known`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ traceId, articleId, productName, category, source, known }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to mark quantity flag known: ${response.statusText}`);
  }
}

/**
 * Get all admin users including demo accounts (for permissions page)
 */
export async function getAllAdminUsers(): Promise<AdminUserStats> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/users`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }

  return response.json();
}

export interface CreateCustomerInput {
  email: string;
  name: string;
  locale: 'nl' | 'en';
  restaurantNames: string[];
  setupFeeCents: number;
  pricePerRestaurantCents: number;
  enableAccountSetup: boolean;
  profilePicture?: File;
}
export interface CreateCustomerResult { userId: string; restaurantIds: string[]; warnings: string[] }

export async function createCustomer(input: CreateCustomerInput): Promise<CreateCustomerResult> {
  const token = await getAccessToken();
  const body = new FormData();
  Object.entries(input).forEach(([key, value]) => {
    if (value === undefined) return;
    body.append(key, value instanceof File ? value : Array.isArray(value) ? JSON.stringify(value) : String(value));
  });
  const response = await fetch(`${API_BASE}/customers`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || 'customer_creation_failed');
  }
  return response.json();
}
